import type { Auth0User } from 'paintscout'
import { USER_COLOR_PALETTE } from '@paintscout/util'

/**
 * Builds the `Auth0User` object passed to `<UserProvider>` by overlaying the live
 * `companyDetails` (from the `appDocs` query) onto the session's token-derived user.
 *
 * `companyDetails` is authoritative for `user.app_metadata.company`: spreading
 * `session.app_metadata.company` under it would leak fields from the logged-in
 * superadmin into a masqueraded view — any field that happens to be unset on the
 * masqueraded company's meta document is `undefined` on `companyDetails`, gets
 * stripped by JSON transport, and therefore cannot override a truthy value
 * inherited from the superadmin's token (e.g. `nonBilling: true` on paintscout's
 * internal admin company hiding the Billing menu for the masqueraded paying
 * customer).
 *
 * If `companyDetails` is missing (shouldn't happen for a well-formed appDocs
 * response), fall back to the session's company so downstream consumers still
 * have a shape to read from.
 *
 * `isOwner` needs the same treatment as `company` above, but for a different reason:
 * it's a flat `app_metadata.isOwner` sibling of `company`, not nested under it, so
 * overriding `company` alone never touches it — it's left as the real logged-in
 * superadmin/support/corporate user's OWN flag, which is `false` for internal staff
 * accounts. That hid owner-only UI (e.g. the self-serve "Cancel Subscription" button,
 * #21879) even while masquerading as an account whose real owner would see it, even
 * though the underlying mutations are already gated server-side by role/permission
 * (not isOwner) and succeed fine for masquerading staff. Only superadmin/support/
 * corporate roles can set masqueradeCompanyId in the first place (see
 * getMasqueradeCompanyId in AuthorizeUser.tsx), so forcing this to `true` while
 * masquerading doesn't grant owner-only capability to anyone who couldn't already
 * reach it.
 */
export function buildUserForProvider({
  session,
  companyDetails,
  isMasquerading
}: {
  session: Auth0User
  companyDetails: Auth0User['app_metadata']['company'] | null | undefined
  isMasquerading?: boolean
}): Auth0User {
  return {
    ...session,
    app_metadata: {
      ...session?.app_metadata,
      company: companyDetails ?? session?.app_metadata?.company,
      isOwner: isMasquerading ? true : session?.app_metadata?.isOwner
    },
    user_metadata: {
      ...session?.user_metadata,
      color: session?.user_metadata?.color || USER_COLOR_PALETTE[0]
    }
  } as Auth0User
}
